import { useEffect, useState } from "react";
import NewUserFormPresenterView from "./NewAdminUserFormPresenterView/NewAdminUserFormView";
import {
  CreateNewUserAdminRequest,
  UserGender,
  UserRole,
  userRoles,
} from "../../../types/user.types";
import  {
  validateEmail,
  validateNotEmptyText,
} from "../../../../../utils/inputValidityChecker";
import useAdminUser from "../../../hooks/useAdminUser";
import useSingleCreateUserLink from "../../../../createUserLink/hooks/useSingleCreateUserLink";
import useSingleOrganization from "../../../../organization/hooks/useSingleOrganization";
import ValidatedStateDisplay from "../../../../../common/components/organisme/ValidatedStateDisplay/ValidatedStateDisplay";

//TYPE
export interface UserFormData {
  email?: string;
  password?: string;
  passwordConfirmation?: string;
  lastname?: string;
  firstname?: string;
  gender?: UserGender;
  organizationId?: string;
  uctId?: string;
  userRole?: UserRole;
  numberPhone?: string;
}

export interface FormError {
  isEmailError: boolean;
  isPasswordError: boolean;
  isPasswordConfirmationError: boolean;
  isLastnameError: boolean;
  isFirstnameError: boolean;
  isGenderError: boolean;
  isOrganizationIdError: boolean;
  allGood: boolean;
  messageError?: string;
  isNumberPhoneError?: boolean;
}

const INITIAL_ERROR_STATUS: FormError = {
  isEmailError: false,
  isPasswordError: false,
  isPasswordConfirmationError: false,
  isLastnameError: false,
  isFirstnameError: false,
  isGenderError: false,
  isOrganizationIdError: false,
  allGood: true,
  messageError: undefined,
  isNumberPhoneError: false,
};

/**
 * Check validity of the form
 * @param userFormData
 * @returns
 */
const checkFormVadilty = (userFormData: UserFormData, userRole: UserRole | undefined | null): FormError => {
  let initialError: FormError = { ...INITIAL_ERROR_STATUS };

  let messageError: string[] = [];
  if (!userFormData.email || !validateEmail(userFormData.email)) {
    initialError.isEmailError = true;
    initialError.allGood = false;
    messageError.push("Email invalide");
  }

  if (!userFormData.password || !validateNotEmptyText(userFormData.password)) {
    initialError.isPasswordError = true;
    initialError.allGood = false;
    messageError.push("Mot de passe invalide");
  }

  if (!userFormData.password?.length || userFormData.password.length < 8) {
    initialError.isPasswordError = true;
    initialError.allGood = false;
    messageError.push("Mot de passe doit contenir au moins 8 caractères");
  }

  if(!userFormData.numberPhone  || userFormData.numberPhone.length < 10){
    initialError.isNumberPhoneError = true;
    initialError.allGood = false;
    messageError.push("Numéro de téléphone invalide");
    
  }

  if (
    !userFormData.passwordConfirmation ||
    userFormData.passwordConfirmation !== userFormData.password
  ) {
    initialError.isPasswordConfirmationError = true;
    initialError.allGood = false;
    messageError.push("Les mots de passe ne correspondent pas");
  }

  if (!userFormData.lastname || !validateNotEmptyText(userFormData.lastname)) {
    initialError.isLastnameError = true;
    initialError.allGood = false;
    messageError.push("Nom invalide");
  }

  if (
    !userFormData.firstname ||
    !validateNotEmptyText(userFormData.firstname)
  ) {
    initialError.isFirstnameError = true;
    initialError.allGood = false;
    messageError.push("Prénom invalide");
  }

  if (!userFormData.gender) {
    initialError.isGenderError = true;
    initialError.allGood = false;
    messageError.push("Genre invalide");
  }

  if (
    userRole !== userRoles.SUPER_ADMIN &&(
    !userFormData.organizationId ||
    !validateNotEmptyText(userFormData.organizationId))
  ) {
    initialError.isOrganizationIdError = true;
    initialError.allGood = false;
    messageError.push("Organisation invalide");
  }

  if (!userFormData.uctId || !validateNotEmptyText(userFormData.uctId)) {
    initialError.isOrganizationIdError = true;
    initialError.allGood = false;
    messageError.push(
      "ID UCT invalide: formulaire invalide, veuillez contactez l'administrateur"
    );
  }

  if (messageError.length > 0) {
    const message = messageError.join(", ") + ".";
    initialError = { ...initialError, messageError: message };
  }

  return initialError;
};
const translateErrorFromBackend = (error: any) => {
  const message = error?.response?.data?.message;
  const status = error?.response?.status;

  if (status === 500) {
    return "Erreur serveur, veuillez réessayer plus tard";
  }
  if (message.includes("CODE_ERROR_APP:1-0")) {
    return "Cette adresse email est déjà utilisée";
  }

  return message || "Erreur lors de la création de l'utilisateur";
};

/**
 * New user form presenter
 *
 * @returns
 */
export default function NewAdminUserFormPresenter({
  culkID,
  withToken,
}: {
  culkID?: string;
  withToken?: boolean;
}) {
  const [isUserGranted, setIsUserGranted] = useState<boolean | undefined>(
    undefined
  );

  const [errorMessage, setErrorMessage] = useState<string | undefined>(
    undefined
  );
  const [userFormData, setUserFormData] = useState<UserFormData>({
    gender: "MALE",
  });

  const [errorStatus, setErrorStatus] = useState<FormError>({
    ...INITIAL_ERROR_STATUS,
  });

  const [isUserExist, setIsUserExist] = useState<boolean>(false);

  const [isDone, setIsDone] = useState<boolean>(false);

  //HOOKS

  const { createNewAdminUserUCTQuery } = useAdminUser({disableGet: true});

  const { singleCreateUserLinkQuery } = useSingleCreateUserLink(culkID);

  //EFFECTS
  useEffect(() => {
    if (!singleCreateUserLinkQuery.data) {
      setErrorMessage("Lien invalide: aucun utilisateur trouvé");
      setIsUserGranted(false);
      return;
    }

    if (singleCreateUserLinkQuery.error) {
      setErrorMessage("Lien invalide: " + singleCreateUserLinkQuery.error);
      setIsUserGranted(false);
      return;
    }

    if (singleCreateUserLinkQuery.data?.uct_status !== "ACTIVATED") {
      setErrorMessage("Lien invalide: utilisateur désactivé");
      setIsUserGranted(false);
      return;
    }

    setUserFormData({
      email: singleCreateUserLinkQuery?.data?.uct_email,
      firstname: singleCreateUserLinkQuery?.data?.uct_firstname,
      lastname: singleCreateUserLinkQuery?.data?.uct_lastname,
      organizationId: singleCreateUserLinkQuery?.data?.uct_organization_id,
      uctId: singleCreateUserLinkQuery?.data?.uct_id,
      gender: singleCreateUserLinkQuery?.data?.uct_user_gender,
      userRole: singleCreateUserLinkQuery?.data?.uct_user_role,
    });
    setErrorMessage(undefined);
    setIsUserGranted(true);
  }, [singleCreateUserLinkQuery.data, singleCreateUserLinkQuery.error]);

  useEffect(() => {

    if (singleCreateUserLinkQuery.data?.uct_user_id_dest && singleCreateUserLinkQuery.data?.uct_user_id_dest !== "") {
      setIsUserExist(true);
    } else {
      setIsUserExist(false);
    }


  },[singleCreateUserLinkQuery.data])

  //Function

  const convertFormToUser = (
    userFormData: UserFormData
  ): CreateNewUserAdminRequest => {
    return {
      user_email: userFormData.email,
      user_firstname: userFormData.firstname,
      user_lastname: userFormData.lastname,
      uct_id: userFormData.uctId as string,
      user_password: userFormData.password || "",
      user_phone_number: userFormData.numberPhone,
    };
  };

  const resetForm = () => {
    setUserFormData({});
    setErrorStatus({ ...INITIAL_ERROR_STATUS });
  };

  const onSubmit = () => {
    const errorStatus = checkFormVadilty({ ...userFormData }, userFormData.userRole);
  
    if (!errorStatus.allGood && !isUserExist) {
      setErrorStatus(errorStatus);
      return;
    }
    const newUserToCreate = convertFormToUser(userFormData);

    createNewAdminUserUCTQuery.mutate({
      newData: {...newUserToCreate },
      callback: (result) => {
        if (result.error) {
          setErrorMessage(translateErrorFromBackend(result.error));
        } else {
          resetForm();
          setIsDone(true);
        }
      },
    });
  };

  if (singleCreateUserLinkQuery.isLoading) {
    return <p >Chargement... <br/><br/>Merci de patienter quelque instant.</p>;
  }
  if (!isUserGranted) {
    return <p>Lien expiré ou invalide, Veuillez contacter votre administrateur.</p>;
  }

  if (isDone) {
    return <ValidatedStateDisplay  
      validationMessage="Votre compte est créé avec succés !"
      labelBtn="Se connecter"
      onClickBtn={() => {
        window.location.href = "/login";
      }}
      marginLeft="-20%"
    />;
  }

  return (
    <NewUserFormPresenterView
      TitleRender={() => (
        <>
          {" "}
          Bienvenue { userFormData?.organizationId  && <> chez {" "}
          <OrganizationName
            organizationId={userFormData?.organizationId || ""}
          /></>}
        </>
      )}
      description="Veuillez remplir les informations suivantes pour créer votre compte"
      isUserExist= {isUserExist}
      userFormData={userFormData}
      setUserFormData={setUserFormData}
      errorStatus={errorStatus}
      messageError={errorStatus.messageError || errorMessage}
      handleSubmit={onSubmit}
    />
  );
}

const OrganizationName = ({ organizationId }: { organizationId: string }) => {
  const { singleOrganizationQuery } = useSingleOrganization(organizationId);

  if (singleOrganizationQuery.isLoading) {
    return <>...</>;
  }

  if (singleOrganizationQuery.error) {
    return <> - </>;
  }

  return <>{singleOrganizationQuery?.data?.organization_name}</>;
};
